import {
	ScoreCriteriaResponse,
	ScoreResponse,
} from "@/pages/Private/pages/Score/schema/score.schema";

/** A segment or persona the user switched off neither needs a scoring nor covers one (PREC-4055). */
const isActive = (row: { active?: boolean }) => row.active !== false;

/**
 * Which parts of the scoring configuration are in use — the inputs of the Lead Scoring nav bubble.
 * Deactivated segments and personas are left out on both sides, exactly as the backend's
 * `isScoreFullyActivated` (score-full-activation.ts) reads them through `activeOnly`: before
 * PREC-4099 the bubble counted them, so switching a segment off made the dashboard call the
 * workspace done while the bubble stayed orange, naming the segment the user had turned off.
 */
export const computeSegmentsUsed = (
	score: ScoreResponse | undefined,
	scoreCriteria: ScoreCriteriaResponse | undefined
) => {
	const allCriteria = scoreCriteria?.allCriteria ?? [];
	const prospectCompanyCategories = (score?.prospectCompanyCategories ?? []).filter(isActive);
	const partnerCompanyCategories = (score?.partnerCompanyCategories ?? []).filter(isActive);
	const prospectPersonaCategories = (score?.prospectPersonaCategories ?? []).filter(isActive);

	// Global sets: company/persona IDs used in ANY lead scoring criteria
	const usedCompanyCategoryIds = new Set(
		allCriteria.flatMap((l) =>
			l.scoreCriteriaCompaniesPersonas.map((sccp) => sccp.scoreCompanyCategoryId)
		)
	);
	const usedPersonaCategoryIds = new Set(
		allCriteria.flatMap((l) =>
			l.scoreCriteriaCompaniesPersonas.map((sccp) => sccp.scorePersonaCategoryId)
		)
	);

	// Every prospect company segment is used in at least one lead scoring
	const isProspectCompanyInUseLeadScoring =
		prospectCompanyCategories.length > 0 &&
		prospectCompanyCategories.every(
			(cc) => cc.id !== undefined && usedCompanyCategoryIds.has(cc.id)
		);

	// Every partner company segment is used in at least one lead scoring
	const isPartnerCompanyInUseLeadScoring =
		!score?.partners ||
		(partnerCompanyCategories.length > 0 &&
			partnerCompanyCategories.every(
				(cc) => cc.id !== undefined && usedCompanyCategoryIds.has(cc.id)
			));

	// Every persona segment is used in at least one lead scoring
	const isProspectPersonaInUseLeadScoring =
		prospectPersonaCategories.length > 0 &&
		prospectPersonaCategories.every(
			(pc) => pc.id !== undefined && usedPersonaCategoryIds.has(pc.id)
		);

	// Every persona is linked to at least one prospect company segment
	const isPersonaLinkedToAnySegment = prospectPersonaCategories.every((pc) => {
		const linkedCompanyIds = new Set(pc.scoreCompanyCategories?.map((cc) => cc.id) ?? []);

		const linkedToProspect = prospectCompanyCategories.some((cc) => linkedCompanyIds.has(cc.id));
		const linkedToPartner =
			!!score?.partners && partnerCompanyCategories.some((cc) => linkedCompanyIds.has(cc.id));
		const linkedToInvestor = !!score?.investors && !!pc.investors;

		return linkedToProspect || linkedToPartner || linkedToInvestor;
	});
	// Every prospect company segment is linked to at least one persona
	const allPersonaLinkedProspectCompanyIds = new Set(
		prospectPersonaCategories.flatMap(
			(pc) =>
				pc.scoreCompanyCategories?.filter((cc) => cc.type === "PROSPECT")?.map((cc) => cc.id) ?? []
		)
	);
	const isProspectCompanyLinkedToPersona =
		prospectCompanyCategories.length > 0 &&
		prospectCompanyCategories.every(
			(cc) => cc.id !== undefined && allPersonaLinkedProspectCompanyIds.has(cc.id)
		);

	// Every partner company segment is linked to at least one persona
	const allPersonaLinkedPartnerCompanyIds = new Set(
		prospectPersonaCategories.flatMap(
			(pc) =>
				pc.scoreCompanyCategories?.filter((cc) => cc.type === "PARTNER")?.map((cc) => cc.id) ?? []
		)
	);
	const isPartnerCompanyLinkedToPersona =
		!score?.partners ||
		(partnerCompanyCategories.length > 0 &&
			partnerCompanyCategories.every(
				(cc) => cc.id !== undefined && allPersonaLinkedPartnerCompanyIds.has(cc.id)
			));

	const isAtLeastOneSegmentGroupValid =
		isProspectCompanyInUseLeadScoring ||
		isPartnerCompanyInUseLeadScoring ||
		isProspectPersonaInUseLeadScoring;

	const hasNoTotallyEmptyGroup =
		(prospectCompanyCategories.length === 0 || isProspectCompanyInUseLeadScoring) &&
		(partnerCompanyCategories.length === 0 ||
			!score?.partners ||
			isPartnerCompanyInUseLeadScoring) &&
		(prospectPersonaCategories.length === 0 || isProspectPersonaInUseLeadScoring);

	const isSegmentsCombinedValid = isAtLeastOneSegmentGroupValid && hasNoTotallyEmptyGroup;

	return {
		isProspectCompanyInUseLeadScoring,
		isPartnerCompanyInUseLeadScoring,
		isProspectPersonaInUseLeadScoring,
		isPersonaLinkedToAnySegment,
		isProspectCompanyLinkedToPersona,
		isPartnerCompanyLinkedToPersona,
		isSegmentsCombinedValid,
	};
};
